import {Capacitor, registerPlugin} from '@capacitor/core';
import {Directory, Filesystem} from '@capacitor/filesystem';
import {Share} from '@capacitor/share';

import {blobToBase64} from '@app/utils/utils';

export type SaveFileResult = 'downloaded' | 'shared' | 'cancelled';

/** Native "Save as…" dialog — android/.../filesaver, ios/App/App/FileSaver. */
const FileSaver = registerPlugin<{
  saveAs(options: { path: string; fileName: string; mimeType: string }): Promise<{ saved: boolean }>;
}>('FileSaver');

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
  zip: 'application/zip',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
};

const sanitizeFileName = (name: string): string =>
  (name || 'file').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'file';

const mimeTypeFor = (blob: Blob, name: string): string => {
  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXTENSION[extension] || blob.type || 'application/octet-stream';
};

/**
 * Save a file the user asked to download.
 *
 * - Web: regular browser download.
 * - Native: writes a temp copy and opens the system "Save as" screen, where the user picks
 *   the folder (Android: Downloads / any folder / Drive; iOS: Files app locations).
 *   Falls back to the share sheet only if the native plugin is missing.
 */
export const saveFile = async (blob: Blob, fileName: string): Promise<SaveFileResult> => {
  const name = sanitizeFileName(fileName);

  if (!Capacitor.isNativePlatform()) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    return 'downloaded';
  }

  const {uri} = await Filesystem.writeFile({
    path: `downloads/${name}`,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  });

  if (Capacitor.isPluginAvailable('FileSaver')) {
    const {saved} = await FileSaver.saveAs({path: uri, fileName: name, mimeType: mimeTypeFor(blob, name)});
    return saved ? 'downloaded' : 'cancelled';
  }

  try {
    await Share.share({title: name, files: [uri], dialogTitle: 'שמירה או שיתוף של הקובץ'});
    return 'shared';
  } catch (e) {
    if (/cancel/i.test(String((e as Error)?.message ?? e))) return 'cancelled';
    throw e;
  }
};

/** Fetch a remote file (customer file, invoice PDF…) and save it via {@link saveFile}. */
export const saveFileFromUrl = async (url: string, fileName: string): Promise<SaveFileResult> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);
  return saveFile(await response.blob(), fileName);
};
